/**
 * Keep `--app-viewport-height` equal to the height the reader can actually see.
 *
 * On iOS Safari `100vh` is the height the viewport would have with the browser
 * chrome collapsed, which it usually is not — trusting it puts the tab bar below
 * the fold on first paint and again every time the address bar animates. The
 * visual viewport is the only measurement that matches what is on screen.
 *
 * WHAT THE VISUAL VIEWPORT IS NOT GOOD FOR is the soft keyboard. It shrinks by
 * the keyboard's height, and iOS hands that shrunken number back — stale, with
 * the keyboard long gone — when the app resumes from the background, with no
 * later resize to correct it. The shell is sized from this property, so
 * believing it leaves the app ending mid-screen with the tab bar floating in the
 * middle of the page.
 *
 * So a viewport unit is the FLOOR, and WHICH unit depends on whether there is
 * any browser chrome to be wrong about.
 *
 * IN A TAB it is `100dvh` — the viewport as the browser's own chrome has left
 * it. It tracks the address bar, which is the whole point of this module, and it
 * is not moved by the keyboard, so it agrees with the visual viewport in every
 * case this module was written for and disagrees only when the keyboard is up or
 * the reading is stale.
 *
 * INSTALLED it is `100lvh`, and the difference is not cosmetic. A home-screen
 * web app has no retractable chrome, but iOS counts the status bar as if it had:
 * `100svh` is the screen MINUS the status bar, and after a resume the dynamic
 * viewport can sit down on that small value and stay there, with no resize to
 * announce it. Every other measurement agrees with it — `innerHeight`, the
 * visual viewport and `100dvh` all report the short number — so nothing in the
 * comparison below can tell that a status bar's worth of screen has gone
 * missing. On a 402x874 iPhone that is 812px of shell on 874px of glass: the app
 * paints its own black in the last 62px and the tab bar floats above the home
 * indicator by exactly the height of the status bar.
 *
 * `100lvh` is the viewport with all of that retractable UI hidden, which is the
 * whole web view when there is no chrome to retract — the one reading iOS does
 * not park at the small value. It is believed ONLY when installed, because in a
 * tab it is the chrome-collapsed height this file opens by refusing to trust.
 */

/**
 * The controls that raise a soft keyboard — deliberately narrow, so a focused
 * checkbox or button, which do not, cannot license a short reading.
 */
const SOFT_KEYBOARD_FOCUS_SELECTOR = [
    'input:not([type="button"]):not([type="submit"]):not([type="reset"])'
        + ':not([type="checkbox"]):not([type="radio"]):not([type="range"])'
        + ':not([type="color"]):not([type="file"])',
    'textarea',
    '[contenteditable]:not([contenteditable="false"])',
].join(',');

export function hasSoftKeyboardFocus(element) {
    return Boolean(
        element
        && typeof element.matches === 'function'
        && element.matches(SOFT_KEYBOARD_FOCUS_SELECTOR),
    );
}

/**
 * Installed to the home screen, by either of the two answers a browser gives.
 * `display-mode` is the standard one; `navigator.standalone` is iOS's own, and
 * still the only one some versions answer honestly.
 */
function isStandaloneDisplay(windowObject) {
    return windowObject.matchMedia('(display-mode: standalone)').matches
        || windowObject.navigator.standalone === true;
}

/**
 * `100dvh` and `100lvh` in pixels. No API reports either, so both come off one
 * probe that exists for a single layout read — one element rather than two, so
 * the two numbers are answers about the same moment. 0 means "not measurable" —
 * jsdom, or a browser without the unit — and the caller then simply has no floor
 * to apply.
 */
function measureViewportUnits(windowObject) {
    const documentElement = windowObject.document.documentElement;
    const probe = windowObject.document.createElement('div');

    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:fixed;top:0;left:0;width:0;'
        + 'visibility:hidden;pointer-events:none;';
    documentElement.appendChild(probe);

    const read = (height) => {
        probe.style.height = height;

        const measured = probe.getBoundingClientRect().height;

        return measured > 0 ? measured : 0;
    };

    const dynamicViewportHeight = read('100dvh');
    const largeViewportHeight = read('100lvh');

    probe.remove();

    return { dynamicViewportHeight, largeViewportHeight };
}

/**
 * The height to publish, in CSS pixels. 0 means nothing was measurable and the
 * caller should leave the property alone rather than pin the shell to zero.
 */
export function resolveViewportHeight({
    visualViewportHeight = 0,
    innerHeight = 0,
    dynamicViewportHeight = 0,
    largeViewportHeight = 0,
    isStandalone = false,
    isSoftKeyboardOpen = false,
} = {}) {
    const toHeight = (value) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0);
    const measured = toHeight(visualViewportHeight) || toHeight(innerHeight);
    // The large viewport is the floor only where there is no chrome for it to
    // have collapsed; everywhere else it is exactly the lie this module opens by
    // naming. Taken as a maximum rather than instead of the dynamic one, so a
    // browser that answers only one of the two units still has a floor.
    const floor = Math.max(
        toHeight(dynamicViewportHeight),
        isStandalone ? toHeight(largeViewportHeight) : 0,
    );

    if (!measured) {
        return floor;
    }

    if (!floor) {
        return measured;
    }

    return measured < floor && !isSoftKeyboardOpen ? floor : measured;
}

/**
 * `trustsSoftKeyboard: false` reads the viewport as if no keyboard were up,
 * whatever holds focus. That is the resume case: the field focused in the
 * previous session is not evidence of a keyboard in this one, and a keyboard
 * that genuinely reopens says so with its own resize.
 */
function syncViewportHeight(windowObject, { trustsSoftKeyboard = true } = {}) {
    const viewportHeight = resolveViewportHeight({
        visualViewportHeight: windowObject.visualViewport?.height,
        innerHeight: windowObject.innerHeight,
        ...measureViewportUnits(windowObject),
        isStandalone: isStandaloneDisplay(windowObject),
        isSoftKeyboardOpen: trustsSoftKeyboard
            && hasSoftKeyboardFocus(windowObject.document.activeElement),
    });

    if (viewportHeight > 0) {
        windowObject.document.documentElement.style.setProperty(
            '--app-viewport-height',
            `${Math.round(viewportHeight)}px`,
        );
    }
}

function syncDisplayModeClasses(windowObject) {
    windowObject.document.documentElement.classList.toggle(
        'is-standalone',
        isStandaloneDisplay(windowObject),
    );
}

export function startViewportSync(windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject) {
        return;
    }

    const onViewportChange = () => {
        syncDisplayModeClasses(windowObject);
        syncViewportHeight(windowObject);
    };

    const resync = () => {
        syncDisplayModeClasses(windowObject);
        syncViewportHeight(windowObject, { trustsSoftKeyboard: false });
        // iOS reports a stale height on the tick it resumes from the background,
        // so re-measure once layout has settled.
        windowObject.requestAnimationFrame(() => {
            syncViewportHeight(windowObject, { trustsSoftKeyboard: false });
        });
    };

    resync();

    windowObject.visualViewport?.addEventListener('resize', onViewportChange);
    windowObject.addEventListener('resize', onViewportChange);
    windowObject.addEventListener('orientationchange', onViewportChange);
    // The keyboard closing is not reliably a resize, and the shell must not stay
    // short once the thing that licensed it being short has gone. Next frame,
    // not this one: during `focusout` the focus has not landed yet, and moving
    // between two fields keeps the keyboard up — only what holds focus after it
    // settles says whether the short reading still stands.
    windowObject.addEventListener('focusout', () => {
        windowObject.requestAnimationFrame(() => syncViewportHeight(windowObject));
    });
    windowObject.addEventListener('pageshow', resync);
    windowObject.document.addEventListener('visibilitychange', () => {
        if (windowObject.document.visibilityState === 'visible') {
            resync();
        }
    });
}
